import {
  mxCell, mxConnectionConstraint, mxConstants, mxGeometry,
} from 'mxgraph-js';
import HCLTreeVertexValue from './HCLTreeVertexValue/HCLTreeVertexValue';
import HCLTreeMxGraph from '../HCLTreeMxGraph/HCLTreeMxGraph';
import ReferenceTypes from './HCLTreeVertexValue/ReferenceTypes';
import BasicEventVertexDetailedShape from '../HCLCellShape/FaultTree/BasicEvent/BasicEventVertexDetailedShape';
import BasicEventVertexDefaultShape from '../HCLCellShape/FaultTree/BasicEvent/BasicEventVertexDefaultShape';

abstract class HCLTreeVertex extends mxCell {
  private key: string;

  private graph: HCLTreeMxGraph;

  public visited = false;

  /**
   * Basic shape parameters, used and optionally overridden by child classes
   * @type {{RADIUS: number, WIDTH: number, HEIGHT: number, GATE: number}}
   */
  static SHAPE = {
    HEIGHT: 128,
    WIDTH: 128,
    RADIUS: 16,
    GATE: 32,
  };

  /**
   * Stringify style dictionary
   * @example
   * // returns "aspect=fixed;autosize=1"
   * mapStyles({ aspect: "fixed", autosize: 1});
   * @param {[index: string]: any} s - style dictionary
   * @return {string}
   */
  static mapStyles = (s: { [index: string]: any }) => {
    const styles = s;
    styles[mxConstants.STYLE_FONTFAMILY] = 'inherit';
    return Object.entries(styles).map(k => (`${k[0]}=${k[1]}`)).join(';');
  };

  constructor(key: string, value: HCLTreeVertexValue) {
    super(value);
    this.key = key;
    this.setVertex(true);
    this.setGeometry(this.getMyGeometry());
    this.setStyle(HCLTreeVertex.mapStyles(this.getMyStyle()));
  }

  /**
   * This determines the geometry of this cell.
   * You can set x, y, width, and height.
   * @return {MxGraphType.mxGeometry}
   */
  abstract getMyGeometry(): mxGeometry;

  /**
   * This determines the style of this cell.
   * @return {[index: string]: any}
   */
  abstract getMyStyle(): { [index: string]: any };

  /**
   * @return {string} - type of vertex (e.g. Basic Event, AND Gate)
   */
  abstract getType(): string;

  /**
   * Define an return an array of source connection constraints for this vertex
   * @return MxGraphType.mxConnectionConstraint[]
   */
  public getSourceConnectionConstraints(): mxConnectionConstraint[] {
    return [];
  }

  /**
   * Define an return an array of target connection constraints for this vertex
   * @return MxGraphType.mxConnectionConstraint[]
   */
  public getTargetConnectionConstraints(): mxConnectionConstraint[] {
    return [];
  }

  /**
   * Returns clone for vertex
   * @return MxGraphType.mxCell
   */
  abstract clone(): mxCell;

  /**
   * @return {MxGraphType.mxCell[]} - children cells
   */
  getChildren(): mxCell[] {
    const children: mxCell[] = [];
    (this.edges || []).forEach((edge) => {
      if (edge.target && edge.target.id !== this.id) {
        children.push(edge.target);
      }
    });
    return children;
  }

  /**
   * @return {[index: string]: any} - JSON that describes this
   */
  toJSON(): { [index: string]: any } {
    return {
      role: 'public',
      ...this.getValue().toJSON(),
    };
  }

  /**
   * @return {[index: string]: any} - JSON that references this
   */
  toReferenceJSON(): { [index: string]: any } {
    return this.toJSON();
  }

  /**
   * @param graph {HCLTreeMxGraph}
   */
  setGraph(graph: HCLTreeMxGraph) {
    this.graph = graph;
  }

  /**
   * @return {HCLTreeMxGraph}
   */
  getGraph(): HCLTreeMxGraph {
    return this.graph;
  }

  /**
   * @return {string}
   */
  getKey(): string {
    return this.key;
  }


  setKey(key: string): void {
    this.key = key;
  }

  getValue(): HCLTreeVertexValue {
    return this.value;
  }

  getParents(): mxCell[] {
    return this.getIncomingEdges().map(edge => edge.source);
  }

  getTooltip(): string {
    return this.getValue().getLabel().getDescription();
  }

  getIncomingEdges(): mxCell[] {
    if (!this.edges) return [];
    return this.edges.filter(edge => edge.target.id === this.id);
  }

  getOutgoingEdges(): mxCell[] {
    if (!this.edges) return [];
    return this.edges.filter(edge => edge.source.id === this.id);
  }

  public abstract getReferenceType(): ReferenceTypes;

  /**
   * Callback function for when this vertex is added to the graph. Arguments contain the graph to which the vertex was
   * added to.
   * @param graph
   */
  public onAddedToGraph(graph: HCLTreeMxGraph): void {
    this.setGraph(graph);
    this.getGeometry().x += -graph.view.translate.x + 100;
    this.getGeometry().y += -graph.view.translate.y + 20;
    this.updateShape(graph.isSubLabelVisible());
  }

  public updateShape(isSubLabelVisible: boolean): void {

  }

  /**
   * Callback function for when this vertex is being requested to be removed from the graph. Arguments contain the graph
   * from which the vertex is being asked to be removed.
   * added to.
   * @param graph
   */
  public willRemoveFromGraph(graph: HCLTreeMxGraph) {

  }

  public onRequestConnectChildVertex(vertex: HCLTreeVertex): mxCell {
    return null;
  }
}

export default HCLTreeVertex;
